// mockJournalData.js
// Mock data pour JournalDemo — à importer et passer via la prop `demoData`.
// Exemple d'utilisation :
//   import mockJournalData from "./mockJournalData";
//   <JournalDemo demoData={mockJournalData} />
//
// Les images pointent vers /demo-foods/<fichier> (dossier public/demo-foods).
// Si tes noms de fichiers diffèrent, adapte simplement les valeurs `image` ci-dessous.

const todayISO = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const withTime = (hh, mm) => `${todayISO()}T${String(hh).padStart(2, "0")}:${String(mm).padStart(2, "0")}:00`;

const mockJournalData = {
  journal: {
    id: "demo-journal-1",
    meals: [
      {
        id: "demo-meal-breakfast",
        name: "Petit-déjeuner",
        mealTime: "07:30",
        sortOrder: 0,
        foods: [
          {
            id: "demo-food-1",
            name: "Flocons d'avoine",
            quantity: 60,
            image: "/demo-foods/oats.svg",
            createdAt: withTime(7, 30),
            macronutrients: {
              "energy-kcal": 379,
              proteins: 13.5,
              carbohydrates: 67.7,
              fat: 6.9,
              fiber: 10.1,
              sugars: 0.99,
              sodium: 0.002,
            },
            ingredients: ["Avoine complète"],
            note: "",
          },
          {
            id: "demo-food-2",
            name: "Banane",
            quantity: 120,
            image: "/demo-foods/banana.svg",
            createdAt: withTime(7, 32),
            macronutrients: {
              "energy-kcal": 89,
              proteins: 1.1,
              carbohydrates: 22.8,
              fat: 0.3,
              fiber: 2.6,
              sugars: 12.2,
              sodium: 0.001,
            },
            ingredients: [],
            note: "",
          },
          {
            id: "demo-food-3",
            name: "Œufs brouillés",
            quantity: 100,
            image: "/demo-foods/eggs.svg",
            createdAt: withTime(7, 35),
            macronutrients: {
              "energy-kcal": 148,
              proteins: 10.6,
              carbohydrates: 1.1,
              fat: 10.9,
              fiber: 0,
              sugars: 1.1,
              sodium: 0.14,
            },
            ingredients: ["Œufs", "Beurre"],
            note: "Cuits à la poêle avec un peu de beurre",
          },
        ],
      },
      {
        id: "demo-meal-lunch",
        name: "Déjeuner",
        mealTime: "12:45",
        sortOrder: 1,
        foods: [
          {
            id: "demo-food-4",
            name: "Blanc de poulet grillé",
            quantity: 150,
            image: "/demo-foods/chicken.svg",
            createdAt: withTime(12, 45),
            macronutrients: {
              "energy-kcal": 165,
              proteins: 31,
              carbohydrates: 0,
              fat: 3.6,
              fiber: 0,
              sugars: 0,
              sodium: 0.074,
            },
            ingredients: ["Blanc de poulet"],
            note: "",
          },
          {
            id: "demo-food-5",
            name: "Riz basmati",
            quantity: 150,
            image: "/demo-foods/rice.svg",
            createdAt: withTime(12, 47),
            macronutrients: {
              "energy-kcal": 130,
              proteins: 2.7,
              carbohydrates: 28,
              fat: 0.3,
              fiber: 0.4,
              sugars: 0.1,
              sodium: 0.001,
            },
            ingredients: ["Riz basmati"],
            note: "",
          },
          {
            id: "demo-food-6",
            name: "Brocoli vapeur",
            quantity: 100,
            image: "/demo-foods/broccoli.svg",
            createdAt: withTime(12, 50),
            macronutrients: {
              "energy-kcal": 35,
              proteins: 2.4,
              carbohydrates: 7.2,
              fat: 0.4,
              fiber: 3.3,
              sugars: 1.7,
              sodium: 0.033,
            },
            ingredients: ["Brocoli"],
            note: "",
          },
        ],
      },
      {
        id: "demo-meal-snack",
        name: "Collation",
        mealTime: "16:30",
        sortOrder: 2,
        foods: [
          {
            id: "demo-food-7",
            name: "Yaourt grec nature",
            quantity: 150,
            image: "/demo-foods/yogurt.svg",
            createdAt: withTime(16, 30),
            macronutrients: {
              "energy-kcal": 97,
              proteins: 9,
              carbohydrates: 3.6,
              fat: 5,
              fiber: 0,
              sugars: 3.6,
              sodium: 0.036,
            },
            ingredients: ["Lait", "Ferments lactiques"],
            note: "",
          },
          {
            id: "demo-food-8",
            name: "Amandes",
            quantity: 30,
            image: "/demo-foods/almonds.svg",
            createdAt: withTime(16, 32),
            macronutrients: {
              "energy-kcal": 579,
              proteins: 21.2,
              carbohydrates: 21.6,
              fat: 49.9,
              fiber: 12.5,
              sugars: 4.4,
              sodium: 0.001,
            },
            ingredients: ["Amandes"],
            note: "",
          },
        ],
      },
      {
        id: "demo-meal-dinner",
        name: "Dîner",
        mealTime: "19:45",
        sortOrder: 3,
        foods: [
          {
            id: "demo-food-9",
            name: "Saumon grillé",
            quantity: 140,
            image: "/demo-foods/salmon.svg",
            createdAt: withTime(19, 45),
            macronutrients: {
              "energy-kcal": 208,
              proteins: 20.4,
              carbohydrates: 0,
              fat: 13.4,
              fiber: 0,
              sugars: 0,
              sodium: 0.059,
            },
            ingredients: ["Saumon"],
            note: "",
          },
          {
            id: "demo-food-10",
            name: "Quinoa",
            quantity: 100,
            image: "/demo-foods/quinoa.svg",
            createdAt: withTime(19, 48),
            macronutrients: {
              "energy-kcal": 120,
              proteins: 4.4,
              carbohydrates: 21.3,
              fat: 1.9,
              fiber: 2.8,
              sugars: 0.9,
              sodium: 0.007,
            },
            ingredients: ["Quinoa"],
            note: "",
          },
          {
            id: "demo-food-11",
            name: "Pomme",
            quantity: 130,
            image: "/demo-foods/apple.svg",
            createdAt: withTime(20, 30),
            macronutrients: {
              "energy-kcal": 52,
              proteins: 0.3,
              carbohydrates: 13.8,
              fat: 0.2,
              fiber: 2.4,
              sugars: 10.4,
              sodium: 0.001,
            },
            ingredients: [],
            note: "",
          },
        ],
      },
    ],
  },
  goals: {
    targetCalories: 2200,
    targetProtein: 140,
    targetCarbs: 250,
    targetFats: 70,
    targetFiber: 30,
    targetSugars: 50,
    targetSodium: 2.3,
    targetSaturatedFat: 20,
    advancedMacroGoalsEnabled: false,
    summaryMacros: ["proteins", "carbohydrates", "fat"],
    displayedMacros: [],
    mainMacros: ["proteins", "carbohydrates", "fat"],
    hideUnavailableFoodMacros: false,
  },
};

export default mockJournalData;
